import React from 'react'
import { Button } from '../ui/Button'
import { useI18n } from '../../i18n'
import { isLinkableAssetId } from '../../services/informationSystemsApi'
import type { AssetOperationalDetail } from '../../services/assetOperationalApi'
import type { InformationSystemSummary } from '../../services/informationSystemsApi'
import intelStyles from '../../styles/components/assets/AssetsInventoryIntelligence.module.scss'
import isStyles from '../../styles/components/assets/AssetsInformationSystems.module.scss'
import { ChipGroup } from './systemPostureRowParts'

interface Props {
  detail: AssetOperationalDetail
  onOpenSystem?: (systemId: string) => void
  informationSystems?: InformationSystemSummary[]
  onLinkToSystem?: (systemId: string) => Promise<void>
  onUnlinkFromSystem?: (systemId: string) => Promise<void>
  linking?: boolean
}

export default function AssetRelationshipsSection({
  detail,
  onOpenSystem,
  informationSystems = [],
  onLinkToSystem,
  onUnlinkFromSystem,
  linking,
}: Props) {
  const { t } = useI18n()
  const [selectedSystemId, setSelectedSystemId] = React.useState('')
  const linkedIds = new Set((detail.linkedSystems || []).map((s) => s.id))
  const linkable = informationSystems.filter((s) => !linkedIds.has(s.id))
  const canLink = isLinkableAssetId(detail.assetId) && onLinkToSystem && linkable.length > 0

  return (
    <section className={intelStyles.relationshipList}>
      <div>
        <h4 className="text-sm font-medium mb-2">{t('assets.assetPassport.linkedSystems', 'Linked information systems')}</h4>
        {(detail.linkedSystems || []).length === 0 ? (
          <p className="text-sm text-gray-500">{t('assets.assetPassport.noLinkedSystems', 'Not linked to any information system.')}</p>
        ) : (
          <ul className={intelStyles.relationshipList}>
            {(detail.linkedSystems || []).map((sys) => (
              <li key={sys.id} className={intelStyles.relationshipCard}>
                <div className="flex flex-wrap items-start justify-between gap-2 w-full">
                  <button
                    type="button"
                    className="text-left flex-1 min-w-0"
                    onClick={() => onOpenSystem?.(sys.id)}
                    disabled={!onOpenSystem}
                  >
                    <strong>{sys.name}</strong>
                    {sys.code ? <span className="text-gray-500 text-sm ml-2">{sys.code}</span> : null}
                    <div className="text-xs text-gray-500 mt-1">
                      {sys.lifecycleStatus || '—'}
                      {sys.relationshipType ? ` · ${sys.relationshipType}` : ''}
                    </div>
                  </button>
                  {onUnlinkFromSystem ? (
                    <Button
                      type="button"
                      size="small"
                      color="outline"
                      disabled={linking}
                      onClick={() => onUnlinkFromSystem(sys.id)}
                    >
                      {t('assets.informationSystems.unlink', 'Unlink')}
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}

        {!isLinkableAssetId(detail.assetId) ? (
          <p className={isStyles.emptyHint}>
            {t(
              'assets.assetPassport.scanAssetNotLinkable',
              'This scan-only asset cannot be linked to an information system. Add it to inventory first.'
            )}
          </p>
        ) : canLink ? (
          <div className={isStyles.assetIsLinkRow}>
            <select
              className={isStyles.assetIsLinkSelect}
              value={selectedSystemId}
              onChange={(e) => setSelectedSystemId(e.target.value)}
            >
              <option value="">{t('assets.assetPassport.selectInformationSystem', 'Select information system…')}</option>
              {linkable.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.code ? ` (${s.code})` : ''}
                </option>
              ))}
            </select>
            <Button
              type="button"
              size="small"
              disabled={!selectedSystemId || linking}
              onClick={() =>
                onLinkToSystem(selectedSystemId).then(() => setSelectedSystemId(''))
              }
            >
              {t('assets.assetPassport.linkToSystem', 'Link to system')}
            </Button>
          </div>
        ) : onLinkToSystem && informationSystems.length === 0 ? (
          <p className={isStyles.emptyHint}>
            {t('assets.assetPassport.noSystemsToLink', 'Create an information system to link this asset.')}
          </p>
        ) : null}
      </div>
      <div>
        <h4 className="text-sm font-medium mb-2">{t('assets.assetPassport.dataAwareness', 'Data awareness')}</h4>
        <ChipGroup
          items={detail.dataCategories || []}
          emptyLabel={t('assets.assetPassport.noDataCategories', 'No data categories inherited')}
          variant="violet"
        />
      </div>
    </section>
  )
}
